import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * General DFW FAQs — client-provided + SEO longtail/voice search optimized
 */
export const generalFAQs = [
  {
    question: "How much does junk removal cost in DFW?",
    answer: "Our junk removal pricing in the surrounding DFW areas is based on volume, weight, and material type. Some services start at $75, with final pricing depending on load size and disposal requirements. For an accurate quote on junk removal, furniture removal, or property cleanouts in DFW, send us a photo of the items and your location — we'll respond quickly with a fast, upfront quote with no hidden fees."
  },
  {
    question: "What junk removal services does Overhaul Ops provide?",
    answer: "Overhaul Ops provides full-service junk removal across the DFW area, including: furniture and appliance removal, garage, attic and storage unit cleanouts, estate and foreclosure cleanouts, move-out and eviction cleanouts, yard debris, brush and dirt removal, construction debris cleanup, and light demolition such as shed removal and carport teardown. From single-item pickups to full property cleanouts, we handle residential and commercial jobs throughout DFW."
  },
  {
    question: "How can I schedule a junk removal appointment?",
    answer: "To schedule your junk removal service in DFW and surrounding areas, you can call or text (817) 682-1555 for a fast response, or fill out our online contact form at OverhaulOps.com to receive a discount on your service. We offer same-day and next-day junk removal appointments in DFW, along with flexible scheduling for future bookings. For the fastest quote and scheduling, send your location and a few photos of what needs removed — we'll take care of the rest."
  },
  {
    question: "How much does mattress removal cost?",
    answer: "Our mattress removal services in DFW and surrounding areas typically range from $50 to $125 per mattress, depending on size (Twin, Queen, King), number of items, and accessibility such as stairs, apartments, or distance. We handle bed disposal, mattress hauling, and bulk item removal quickly and responsibly."
  },
  {
    question: "Do you remove hazardous materials like paint, oil, or batteries?",
    answer: "For safety and environmental compliance, we do not handle most hazardous waste disposal in Fort Worth and the DFW area, including paint, oil, and chemicals. However, we do accept car batteries as long as they are not damaged or leaking, with a $35 disposal fee per battery. If you're in Fort Worth or surrounding DFW areas, we can also point you to the proper hazardous waste disposal facilities and city-approved drop-off locations for restricted materials."
  },
  {
    question: "Do you offer same-day junk removal in DFW?",
    answer: "Yes, Overhaul Ops offers same-day and next-day junk removal appointments throughout the DFW metroplex. If you need junk removed today, call or text us at (817) 682-1555 and we'll do our best to get a crew to your location as quickly as possible. We also offer flexible scheduling for planned cleanouts and larger projects."
  },
  {
    question: "What areas does Overhaul Ops serve?",
    answer: "Overhaul Ops provides junk removal, hauling, cleanouts, and light demolition services across the entire DFW metroplex, including Fort Worth, Arlington, Denton, Keller, Southlake, Grapevine, Weatherford, Saginaw, Justin, Crowley, and over 30 surrounding cities. Whether you're in Tarrant, Denton, or Parker County, we've got you covered."
  },
  {
    question: "Do I need to be home during junk removal?",
    answer: "While it's not always necessary to be home, we recommend being present so you can point out exactly what needs to go. If you can't be there, you can arrange access ahead of time and send us photos with clear instructions. We'll confirm everything before we start and send you a completion photo when we're done."
  },
  {
    question: "How long does a typical junk removal job take?",
    answer: "Most single-item and small junk removal jobs take 15 to 30 minutes. Larger cleanouts like garages, attics, or full property cleanouts can take 1 to 4 hours depending on the volume and accessibility. We work efficiently to minimize disruption while ensuring the job is done right."
  },
  {
    question: "What happens to my junk after you pick it up?",
    answer: "Overhaul Ops is committed to responsible disposal. We sort items for recycling, donation, and proper disposal. Usable items in good condition are donated to local charities whenever possible. Materials like metal, wood, and cardboard are recycled. Only items that can't be reused or recycled go to approved disposal facilities."
  },
  {
    question: "Are you licensed and insured for junk removal?",
    answer: "Yes, Overhaul Ops LLC is fully licensed and insured. We carry general liability insurance to protect your property during every job. As a firefighter-owned business, safety and professionalism are core to everything we do."
  },
  {
    question: "How much does a full garage cleanout cost?",
    answer: "A full garage cleanout in the DFW area typically ranges from $250 to $600 or more, depending on the volume of items, weight of materials, and disposal requirements. For an accurate quote, send us photos of your garage and your location — we provide fast, no-obligation estimates with transparent pricing."
  },
  {
    question: "Can you remove old appliances like refrigerators and washers?",
    answer: "Yes, we handle appliance removal throughout DFW, including refrigerators, washers, dryers, ovens, dishwashers, water heaters, and more. We ensure proper disposal and recycling of appliances in compliance with local regulations. Single appliance pickups start at $75."
  },
  {
    question: "Do you do commercial junk removal for businesses?",
    answer: "Absolutely. Overhaul Ops provides commercial junk removal services for offices, warehouses, retail spaces, and construction sites across DFW. We handle office furniture removal, warehouse cleanouts, construction debris cleanup, and ongoing commercial waste needs. Contact us for commercial pricing."
  },
  {
    question: "What is the cheapest way to get rid of junk in Fort Worth?",
    answer: "The most affordable junk removal option depends on the volume. For small loads, our services start at just $75. For larger cleanouts, our volume-based pricing is often more cost-effective than renting a dumpster when you factor in your time, labor, and disposal fees. Call or text (817) 682-1555 for a free quote — we'll give you an honest, upfront price."
  }
];

/**
 * Generate city-specific FAQs for geo landing pages
 */
export function getCityFAQs(cityName, countyName) {
  return [
    {
      question: `How much does junk removal cost in ${cityName}?`,
      answer: `Our junk removal pricing in ${cityName} and the surrounding DFW areas is based on volume, weight, and material type. Some services start at $75, with final pricing depending on load size and disposal requirements. For an accurate quote on junk removal, furniture removal, or property cleanouts in ${cityName}, send us a photo of the items and your location — we'll respond quickly with a fast, upfront quote with no hidden fees.`
    },
    {
      question: `What junk removal services are available in ${cityName}?`,
      answer: `Overhaul Ops provides full-service junk removal in ${cityName}, ${countyName || "TX"}, including: furniture and appliance removal, garage, attic and storage unit cleanouts, estate and foreclosure cleanouts, move-out and eviction cleanouts, yard debris, brush and dirt removal, construction debris cleanup, and light demolition such as shed removal and carport teardown.`
    },
    {
      question: `How do I schedule junk removal in ${cityName}?`,
      answer: `To schedule your junk removal service in ${cityName}, call or text (817) 682-1555 for a fast response, or fill out our online contact form at OverhaulOps.com. We offer same-day and next-day junk removal appointments in ${cityName} and throughout DFW, along with flexible scheduling for future bookings.`
    },
    {
      question: `How much does mattress removal cost in ${cityName}?`,
      answer: `Mattress removal in ${cityName} typically ranges from $50 to $125 per mattress, depending on size (Twin, Queen, King), number of items, and accessibility. We handle bed disposal, mattress hauling, and bulk item removal quickly and responsibly across ${cityName} and the DFW metroplex.`
    },
    {
      question: `Do you handle hazardous waste removal in ${cityName}?`,
      answer: `For safety and environmental compliance, we do not handle most hazardous waste in ${cityName}, including paint, oil, and chemicals. However, we do accept car batteries ($35 disposal fee per battery) as long as they are not damaged or leaking. We can point you to proper hazardous waste disposal facilities near ${cityName}.`
    },
    {
      question: `Do you offer same-day junk removal in ${cityName}?`,
      answer: `Yes, Overhaul Ops offers same-day and next-day junk removal in ${cityName}. Call or text (817) 682-1555 and we'll do our best to get a crew to your ${cityName} location as quickly as possible.`
    },
    {
      question: `Are you licensed and insured for junk removal in ${cityName}?`,
      answer: `Yes, Overhaul Ops LLC is fully licensed and insured for all junk removal, hauling, and demolition work in ${cityName} and throughout the DFW metroplex. As a firefighter-owned business, safety and professionalism are our top priorities.`
    },
    {
      question: `How much does a garage cleanout cost in ${cityName}?`,
      answer: `A full garage cleanout in ${cityName} typically ranges from $250 to $600 or more, depending on the volume, weight, and disposal requirements. Send us photos for a fast, no-obligation quote with transparent pricing.`
    }
  ];
}

/**
 * Accordion FAQ component
 */
export default function FAQSection({ faqs, title, subtitle, cityName }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section data-testid="faq-section" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
            FAQ
          </span>
          <h2
            className="text-3xl md:text-4xl font-black text-[#0f0f0f] uppercase tracking-wide mt-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {title || `Frequently Asked Questions`}
          </h2>
          {subtitle && (
            <p className="text-[#4a4a4a] mt-3 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              data-testid={`faq-item-${index}`}
              className="border border-gray-200 rounded-sm overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-bold text-[#0f0f0f] pr-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-[#b51a00] flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-6 pb-5 text-[#4a4a4a] leading-relaxed text-sm">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
